import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Link, Spinner } from "@abb/abb-common-ux-react";
import { WNT_ERROR_LIST } from "../../../strings";

// Import configuration and utilities
import config from "./forecast-config.json";
import { useForecastData } from "./useForecastData";
import { useEChart } from "./useEChart";
import { 
  processChartData, 
  generateChartOptions, 
  validateConfig 
} from "./forecast-utils";

// Import component styles
import "./forecast.css";

/**
 * Optimized Probabilistic Forecast Chart Component
 * Features:
 * - Modular configuration via JSON
 * - Mock data support for development/testing
 * - Custom hooks for data management and chart rendering
 * - Performance optimizations with React.memo
 * - Proper error handling and retry mechanisms
 * - Accessibility support
 * - Responsive design with CSS
 */
function ProbabilisticForecastChart({ 
  isActive = true, 
  customConfig = null,
  onDataUpdate = null,
  className = "",
  style = {}
}) {
  // Use custom config if provided, otherwise use default
  const activeConfig = customConfig || config;

  // Validate configuration
  const isConfigValid = useMemo(() => {
    return validateConfig(activeConfig);
  }, [activeConfig]);

  // Custom hook for data management
  const { 
    data, 
    isLoading, 
    error, 
    lastUpdated, 
    retry 
  } = useForecastData(activeConfig, isActive && isConfigValid);

  // Process data for chart consumption
  const processedData = useMemo(() => {
    const result = processChartData(data, activeConfig);
    
    // Call optional callback when data updates
    if (onDataUpdate && result) {
      onDataUpdate(result);
    }
    
    return result;
  }, [data, activeConfig, onDataUpdate]);

  // Generate chart options
  const chartOptions = useMemo(() => {
    return generateChartOptions(processedData, activeConfig);
  }, [processedData, activeConfig]);

  // Custom hook for chart management
  const { chartRef } = useEChart(chartOptions, [processedData]);

  // Don't render if config is invalid
  if (!isConfigValid) {
    return (
      <div className={`forecast-container forecast-error ${className}`} style={style}>
        <div className="forecast-error-content">
          <span>Invalid configuration</span>
        </div>
      </div>
    );
  }

  // Render loading state
  if (isLoading) {
    return (
      <div className={`forecast-container forecast-loading ${className}`} style={style}>
        <Spinner 
          className="spinner" 
          color="dark-grey" 
          size="small" 
          aria-label="Loading forecast data"
        />
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={`forecast-container forecast-error ${className}`} style={style}>
        <div className="forecast-error-content">
          <span>{error.message || activeConfig.errorMessages?.fetchError || WNT_ERROR_LIST.ERROR_FETCH_DATA}</span>
          <Link 
            className="forecast-retry-link"
            onClick={retry}
            aria-label={activeConfig.accessibility?.retryButtonLabel || "Retry loading forecast data"}
          >
            Retry
          </Link>
        </div>
      </div>
    );
  }

  // Render no data state
  if (!data || data.length === 0) {
    return (
      <div className={`forecast-container forecast-no-data ${className}`} style={style}>
        <p>
          {activeConfig.errorMessages?.noData || WNT_ERROR_LIST.NO_DATA || "No forecast data available"}
        </p>
      </div>
    );
  }

  // Render chart
  return (
    <div 
      className={`forecast-container forecast-chart ${className}`} 
      style={style}
      role="application"
      aria-label={activeConfig.accessibility?.chartDescription || "Probabilistic forecast chart"}
    >
      <div 
        ref={chartRef} 
        className="forecast-chart-container"
      />
      <div className="forecast-last-updated">
        Last updated {lastUpdated}
      </div>
    </div>
  );
}

// PropTypes for development
ProbabilisticForecastChart.propTypes = {
  isActive: PropTypes.bool,
  customConfig: PropTypes.object,
  onDataUpdate: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object
};

// Default props
ProbabilisticForecastChart.defaultProps = {
  isActive: true,
  customConfig: null,
  onDataUpdate: null,
  className: "",
  style: {}
};

// Memoize component for performance
export default React.memo(ProbabilisticForecastChart);